import React from 'react';

import AtIcon from 'svg/AtIcon';
import AttachFileIcon from 'svg/AttachFileIcon';
import BellIcon from 'svg/BellIcon';
import CalendarCheckIcon from 'svg/CalendarCheckIcon';
import CalendarClockIcon from 'svg/CalendarClockIcon';
import ChatBubbleIcon from 'svg/ChatBubbleIcon';
import CircleCheckOutlineIcon from 'svg/CircleCheckOutlineIcon';
import CircleReturnArrowIcon from 'svg/CircleReturnArrowIcon';
import CircleXIconOutlined from 'svg/CircleXIconOutlined';
import ClipboardLinesIcon from 'svg/ClipboardLinesIcon';
import CoordinateRequestOutlineIcon from 'svg/CoordinateRequestOutlineIcon';
import HourglassIcon from 'svg/HourglassIcon';
import LocationPinIcon from 'svg/LocationPinIcon';
import PatientAddedToQueueIcon from 'svg/PatientAddedToQueueIcon';
import PatientDischargedIcon from 'svg/PatientDischargedIcon';
import PersonProhibitedIcon from 'svg/PersonProhibitedIcon';
import PriorityFlag from 'svg/PriorityFlag';
import ReferralOutlineIcon from 'svg/ReferralOutlineIcon';
import SquareExclamationIcon from 'svg/SquareExclamationIcon';
import ThumbsDownOutlineIcon from 'svg/ThumbsDownOutlineIcon';
import ThumbsUpOutlineIcon from 'svg/ThumbsUpOutlineIcon';
import WarnIcon from 'svg/WarnIcon';

// The complete chip palette (OLIO-3059 ICON_MAP.md; yellow added by OLIO-3231): every kind's
// chip is one of exactly four hues. Blue/green/red are design-sourced values (from Taylor's
// exported glyphs/rects), NOT the app's semantic theme colors — the notification chips are their
// own closed palette. Yellow is the email banner family's warning token (Theme::Colors::YELLOW_100
// == colors.ts yellow100), so a flagged chip matches the same moment's email banner.
export const CHIP_COLORS = {
  blue: '#1283DA',
  green: '#11AF22',
  red: '#D93F4C',
  yellow: '#E69200',
} as const;

// Semantic tone drives the chip color: positive acknowledgements are green, negative events
// (denials, readmissions, refusals, off-track) are red, flagged-for-attention outcomes are
// yellow (OLIO-3231 variants), and everything else — informational and time-sensitive alike —
// is blue. The chip is a rounded square with the kind's color at 10% opacity behind the glyph
// (ICON_MAP.md chip recipe).
export enum NotificationTone {
  Info = 'info',
  Positive = 'positive',
  Negative = 'negative',
  Warning = 'warning',
}

type ToneColors = { color: string; background: string };

const TONE_COLORS: Record<NotificationTone, ToneColors> = {
  [NotificationTone.Info]: { color: CHIP_COLORS.blue, background: 'rgba(18, 131, 218, 0.1)' },
  [NotificationTone.Positive]: { color: CHIP_COLORS.green, background: 'rgba(17, 175, 34, 0.1)' },
  [NotificationTone.Negative]: { color: CHIP_COLORS.red, background: 'rgba(217, 63, 76, 0.1)' },
  [NotificationTone.Warning]: { color: CHIP_COLORS.yellow, background: 'rgba(230, 146, 0, 0.1)' },
};

type NotificationTypeConfig = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  tone: NotificationTone;
};

// `kind` → chip icon + tone. Keyed by the NotificationEvent.kind the API sends (Appendix A of the
// tech design; the five previously-empty Figma icon groups now use Taylor's exported glyphs per
// ICON_MAP.md). Unknown/new kinds fall back to a neutral bell (DEFAULT_NOTIFICATION_TYPE), so a
// renderer shipped ahead of a FE map entry still renders a sensible row (wave-2 safety).
export const NOTIFICATION_TYPES: Record<string, NotificationTypeConfig> = {
  // mention
  new_mention: { Icon: AtIcon, tone: NotificationTone.Info },
  new_workflow_action_mention: { Icon: AtIcon, tone: NotificationTone.Info },
  new_chat_mention: { Icon: ChatBubbleIcon, tone: NotificationTone.Info },

  // critical_alerts — acknowledgements (positive)
  escalation_acknowledgement: { Icon: CircleCheckOutlineIcon, tone: NotificationTone.Positive },
  priority_note_acknowledgement: { Icon: CircleCheckOutlineIcon, tone: NotificationTone.Positive },
  // Same circled return-arrow as safe_return_requested (Taylor's exports carried the glyph twice —
  // once per safe-return kind; resolved 2026-08-31, superseding the clipboard-check provisional).
  safe_return_acknowledged: { Icon: CircleReturnArrowIcon, tone: NotificationTone.Positive },

  // critical_alerts — escalations / requests
  escalations: { Icon: WarnIcon, tone: NotificationTone.Negative },
  priority_note_added: { Icon: PriorityFlag, tone: NotificationTone.Negative },
  // ICON_MAP glyph: circled return arrow (green).
  safe_return_requested: { Icon: CircleReturnArrowIcon, tone: NotificationTone.Positive },
  // ICON_MAP glyph: square + exclamation (red).
  readmission: { Icon: SquareExclamationIcon, tone: NotificationTone.Negative },
  // ICON_MAP glyph: person + prohibition sign (red).
  refusal_of_home_health_services: { Icon: PersonProhibitedIcon, tone: NotificationTone.Negative },

  // critical_alerts — time-sensitive dates (blue: informational, per the calendar-clock glyph)
  initial_authorization_nearing_valid_through_date: { Icon: HourglassIcon, tone: NotificationTone.Info },
  // ICON_MAP glyph: calendar + clock (blue) — shared by both "nearing date" kinds.
  patient_nearing_next_review_date: { Icon: CalendarClockIcon, tone: NotificationTone.Info },
  patient_nearing_projected_discharge: { Icon: CalendarClockIcon, tone: NotificationTone.Info },

  // critical_alerts / activity — informational
  patient_moved_to_admitted_and_in_treatment: { Icon: LocationPinIcon, tone: NotificationTone.Info },
  patient_added_to_queue: { Icon: PatientAddedToQueueIcon, tone: NotificationTone.Info },
  patient_discharged: { Icon: PatientDischargedIcon, tone: NotificationTone.Info },
  // Calendar + checkmark, green (Taylor, 2026-08-31): the calendar base with a check badge.
  authorization_number_added: { Icon: CalendarCheckIcon, tone: NotificationTone.Positive },
  new_document_attached: { Icon: AttachFileIcon, tone: NotificationTone.Info },
  new_information_added: { Icon: ClipboardLinesIcon, tone: NotificationTone.Info },
  coordinate_request_submitted: { Icon: CoordinateRequestOutlineIcon, tone: NotificationTone.Info },

  // activity — flagged for attention: the discharge glyph, yellow because staff should check it
  auto_discharge_needs_review: { Icon: PatientDischargedIcon, tone: NotificationTone.Warning },

  // referrals
  referral_new_information_added: { Icon: ClipboardLinesIcon, tone: NotificationTone.Info },
  referral_created: { Icon: ReferralOutlineIcon, tone: NotificationTone.Info },
  referral_accepted: { Icon: ReferralOutlineIcon, tone: NotificationTone.Positive },
  referral_denied: { Icon: ReferralOutlineIcon, tone: NotificationTone.Negative },

  // patient_status_changes
  status_improving: { Icon: ThumbsUpOutlineIcon, tone: NotificationTone.Positive },
  status_declining: { Icon: ThumbsDownOutlineIcon, tone: NotificationTone.Negative },

  // inbound referrals (OLIO-3231) — the ten wave-2 kinds, reusing wave-1 glyphs on their
  // established semantics; tone does the differentiation within a shared glyph, the same move as
  // the outbound referral trio above. Zero new SVGs; a design revision lands as a follow-up PR.
  // Ten, not eleven: referral_tasks left the family in this PR (the digest belongs to the
  // Referral Tasks product and its own product access), so it has no chip and no prefs section.
  new_referral: { Icon: ReferralOutlineIcon, tone: NotificationTone.Info },
  missing_information: { Icon: SquareExclamationIcon, tone: NotificationTone.Info },
  auto_checks: { Icon: CircleCheckOutlineIcon, tone: NotificationTone.Info },
  // The auto-check section's note, under its section's glyph — the same one-section-one-glyph rule
  // the clinical assessment's note follows below.
  new_benefit_verification_note: { Icon: CircleCheckOutlineIcon, tone: NotificationTone.Info },
  benefit_verification_complete: { Icon: CircleCheckOutlineIcon, tone: NotificationTone.Info },
  // One section, one glyph (the referral-trio precedent): assessment results and their notes.
  clinical_assessments: { Icon: ClipboardLinesIcon, tone: NotificationTone.Info },
  new_assessment_note: { Icon: ClipboardLinesIcon, tone: NotificationTone.Info },
  facility_accepted: { Icon: ReferralOutlineIcon, tone: NotificationTone.Positive },
  facility_denied: { Icon: ReferralOutlineIcon, tone: NotificationTone.Negative },
  // Selected/not-selected as check/X (Taylor, 2026-09-18), superseding the thumbs pair — the
  // outcome is a selection result, not a status trend, and the check/X pair reads it at a glance.
  referral_won: { Icon: CircleCheckOutlineIcon, tone: NotificationTone.Positive },
  referral_lost: { Icon: CircleXIconOutlined, tone: NotificationTone.Negative },
  new_chat_message: { Icon: ChatBubbleIcon, tone: NotificationTone.Info },
};

// `tone` → chip override (OLIO-3231). The api stamps a NotificationEvent with the email banner
// vocabulary (warning/error/success; info and NULL mean "the kind's normal presentation") — its
// renderers translate their domain variants exactly as the email templates do — so the chip
// tones the way the same moment's email banner is toned, and a new variant-carrying kind needs
// no FE change at all. One global table, not per-kind: a warning looks like a warning
// everywhere. Glyphs reuse the wave-1 chip vocabulary (readmission's exclamation, escalations'
// warn, the acknowledgements' check); flagged and errored differ in drawing AND color, the same
// two-glyph distinction the email banners draw.
export const TONE_OVERRIDES: Record<string, NotificationTypeConfig> = {
  warning: { Icon: SquareExclamationIcon, tone: NotificationTone.Warning },
  error: { Icon: WarnIcon, tone: NotificationTone.Negative },
  success: { Icon: CircleCheckOutlineIcon, tone: NotificationTone.Positive },
};

export const DEFAULT_NOTIFICATION_TYPE: NotificationTypeConfig = {
  Icon: BellIcon,
  tone: NotificationTone.Info,
};

export type NotificationVisual = { Icon: React.FC<React.SVGProps<SVGSVGElement>> } & ToneColors;

// Resolve a `kind` (+ its event's stamped tone, when present) to its chip icon + colors: the
// tone override first, then the kind's entry, then the neutral default. An info or unknown
// tone falls through to the kind — info IS the kind's normal presentation.
export function getNotificationVisual(kind: string, tone?: string | null): NotificationVisual {
  const config = (tone && TONE_OVERRIDES[tone]) || NOTIFICATION_TYPES[kind] || DEFAULT_NOTIFICATION_TYPE;
  return { Icon: config.Icon, ...TONE_COLORS[config.tone] };
}
